import { useNavigate } from '@tanstack/react-router';
import { ReactNode, useState } from 'react';

import { Button } from '../../cdl/Button/Button';
import { DropdownMenu } from '../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../cdl/IconButton/IconButton';
import { useToasts } from '../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { CancelPopup } from '../../common/CancelPopup/CancelPopup';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { IconCancel } from '../../common/icons/cdl/Cancel';
import { IconCopy } from '../../common/icons/cdl/Copy';
import { IconDots } from '../../common/icons/cdl/Dots';
import { IconDownload } from '../../common/icons/cdl/Download';
import { IconExport } from '../../common/icons/cdl/Export';
import { IconHistory } from '../../common/icons/cdl/History';
import { IconListSearch } from '../../common/icons/cdl/ListSearch';
import { IconMessageCircle } from '../../common/icons/cdl/MessageCircle';
import { IconPencil } from '../../common/icons/cdl/Pencil';
import { IconRestore } from '../../common/icons/cdl/Restore';
import { ShortcutMenuContextAction } from '../../common/ShortcutMenu/components/ShortcutMenuContext';
import { useRegisterShortcutMenuContextActions } from '../../common/ShortcutMenu/hooks/useRegisterShortcutMenuContextActions';
import { EnquiryType, OfferModel } from '../model/OfferModel';

import { ShareOrderPopup } from './common/components/ShareOrderPopup';
import { SimpleConfirmPopup } from './common/components/SimpleConfirmPopup';
import { getOfferCancelPopupText } from './common/helpers/getOfferCancelPopupText';
import { isAdminReopenableOffer } from './common/helpers/isAdminReopenableOffer';
import { useMessagesExport } from './common/hooks/useMessagesExport';
import { useNavigateToOfferEdit } from './common/hooks/useNavigateToOfferEdit';
import { useOfferExport } from './common/hooks/useOfferExport';
import { useOfferReopen } from './common/hooks/useOfferReopen';
import { useOfferUpdate } from './common/hooks/useOfferUpdate';
import { useVesselEdit } from './common/hooks/useVesselEdit';
import { OfferHistoryOverlay } from './history/OfferHistoryOverlay';
import { InitialEnquiryPopup } from './InitialEnquiryPopup';
import { selectEntityNameForOffer } from './selectEntityNameForOffer';

interface OfferDetailActionBarProps {
    offer: OfferModel;
    statusAction?: ReactNode;
}

interface OfferDetailAction {
    id: string;
    label: string;
    onSelect: () => void;
    Icon?: ShortcutMenuContextAction['Icon'];
    destructive?: boolean;
    includeInDropdown?: boolean;
}

export const OfferDetailActionBar = ({ offer, statusAction }: OfferDetailActionBarProps) => {
    const [showInitialEnquiryPopup, setShowInitialEnquiryPopup] = useState(false);
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    const [showReopenPopup, setShowReopenPopup] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [showHistoryOverlay, setShowHistoryOverlay] = useState(false);
    const { addLoadingToast } = useToasts();
    const updateOfferMutation = useOfferUpdate();
    const reopenOfferMutation = useOfferReopen();
    const role = useRole();
    const navigate = useNavigate();
    const navigateToOfferEdit = useNavigateToOfferEdit();
    const vesselEditMutation = useVesselEdit(offer.vesselId);
    const { downloadPdf } = useOfferExport();
    const { downloadMessages } = useMessagesExport(offer.id);

    const hasOfferWriteRights = (currentOffer: OfferModel) =>
        role.hasWriteRights(currentOffer.customerId) || role.hasWriteRights(currentOffer.supplierId);

    const determineCancelState = (currentState: string) => {
        const enquiryStates = ['ENQUIRY', 'QUOTED'];

        if (enquiryStates.includes(currentState)) {
            if (role.isSupplier()) {
                return 'ENQUIRY_DECLINED';
            }

            if (currentState === 'ENQUIRY') {
                return 'ENQUIRY_CANCELED';
            }

            return 'QUOTE_CANCELED';
        }

        return 'CANCELED';
    };

    const onCancelPopupSubmit = async ({ cancelReason }: { cancelReason: string }) => {
        const newState = determineCancelState(offer.state);
        await updateOfferMutation.mutateAsync({
            offer: {
                ...offer,
                state: newState,
                cancelReason,
            },
            offerId: offer.id,
        });

        setShowCancelPopup(false);
    };

    const onReopenPopupSubmit = async () => {
        await reopenOfferMutation.mutateAsync({ offerId: offer.id });
        setShowReopenPopup(false);
    };

    const onShareOrderSuccess = (vesselEmailAddress?: string) => {
        setShowSharePopup(false);

        if (!vesselEmailAddress) {
            return;
        }

        vesselEditMutation.mutate({
            vesselId: offer.vesselId,
            vessel: {
                ...offer.vessel,
                email: vesselEmailAddress,
            },
        });
    };

    const canEdit = !role.isSuperAdmin() && offer.isEditable() && hasOfferWriteRights(offer);
    const canShare = role.isCustomer();
    const actions: OfferDetailAction[] = [];

    if (canShare) {
        actions.push({
            id: 'share-order',
            label: translate('offer.detail.header.actions.share'),
            Icon: IconExport,
            onSelect: () => setShowSharePopup(true),
        });
    }

    if (canEdit) {
        actions.push({
            id: 'edit-offer',
            label: translate('actions.edit'),
            Icon: IconPencil,
            onSelect: () => navigateToOfferEdit(offer.id),
        });
    }

    if (role.isCustomer() && role.hasWriteRights(offer.customerId)) {
        actions.push({
            id: 'copy-offer',
            label: translate('offer.detail.header.actions.copy'),
            Icon: IconCopy,
            includeInDropdown: true,
            onSelect: () =>
                navigate({
                    to: '/orders/new',
                    state: {
                        order: {
                            vessel: offer.vessel,
                            vesselId: offer.vesselId,
                            dateDelivery: offer.dateDelivery,
                            agentId: offer.agentId,
                            customerId: offer.customerId,
                            portId: offer.portId,
                            port: offer.port,
                            buyerReference: offer.buyerReference,
                            items: offer.items,
                            samplekits: offer.samplekits,
                            receiverSupplierIds: [offer.supplierId],
                            spot: offer.enquiryType === EnquiryType.SPOT,
                        },
                    },
                }),
        });
    }

    actions.push({
        id: 'initial-order',
        label: translate('offer.detail.header.actions.initialOrder'),
        Icon: IconExport,
        includeInDropdown: true,
        onSelect: () => {
            if (role.isSupplier() && offer.requestForQuoteId) {
                navigate({
                    to: '/requests-for-quotes/$id',
                    params: { id: offer.requestForQuoteId },
                });
                return;
            }

            setShowInitialEnquiryPopup(true);
        },
    });

    if (role.isSuperAdmin()) {
        if (isAdminReopenableOffer(offer)) {
            actions.push({
                id: 'reopen-enquiry',
                label: translate('offer.detail.header.actions.reopenEnquiry'),
                Icon: IconRestore,
                includeInDropdown: true,
                onSelect: () => setShowReopenPopup(true),
            });
        }

        actions.push({
            id: 'offer-history',
            label: translate('offer.detail.header.actions.history'),
            Icon: IconHistory,
            includeInDropdown: true,
            onSelect: () => setShowHistoryOverlay(true),
        });
    }

    if (!role.isSupplier()) {
        actions.push({
            id: 'spot-overview',
            label: translate('offer.detail.header.actions.spotOverview'),
            Icon: IconListSearch,
            includeInDropdown: true,
            onSelect: () =>
                navigate({
                    to: '/order/$id',
                    params: {
                        id: offer.orderId,
                    },
                }),
        });
    }

    actions.push({
        id: 'export-pdf',
        label: selectEntityNameForOffer(offer, {
            enquiry: translate('offer.detail.header.actions.export.pdf.enquiry'),
            order: translate('offer.detail.header.actions.export.pdf.order'),
        }),
        Icon: IconDownload,
        includeInDropdown: true,
        onSelect: () =>
            addLoadingToast(
                () => downloadPdf(offer.id),
                translate('offer.detail.header.actions.export.downloadingPdf'),
                translate('offer.detail.header.actions.export.success'),
                translate('global.genericToastError')
            ),
    });

    actions.push({
        id: 'export-messages',
        label: translate('offer.detail.header.actions.export.messages'),
        Icon: IconMessageCircle,
        includeInDropdown: true,
        onSelect: () => downloadMessages(offer.offerNumber),
    });

    if (role.isCustomer() && !offer.isFinished() && hasOfferWriteRights(offer)) {
        actions.push({
            id: 'cancel-offer-customer',
            label: translate('offer.detail.header.actions.cancel'),
            Icon: IconCancel,
            includeInDropdown: true,
            destructive: true,
            onSelect: () => setShowCancelPopup(true),
        });
    }

    if (role.isSupplier() && !offer.isFinished() && hasOfferWriteRights(offer)) {
        actions.push({
            id: 'cancel-offer-supplier',
            label:
                offer.state === 'QUOTED'
                    ? translate('offer.detail.header.actions.cancel')
                    : translate('offer.detail.header.actions.decline'),
            Icon: IconCancel,
            includeInDropdown: true,
            destructive: true,
            onSelect: () => setShowCancelPopup(true),
        });
    }

    useRegisterShortcutMenuContextActions(
        actions.map((action) => ({
            id: action.id,
            label: action.label,
            onSelect: action.onSelect,
            Icon: action.Icon,
            destructive: action.destructive,
        }))
    );

    return (
        <>
            <ButtonGroup>
                {canShare ? (
                    <Button LeadingIcon={IconExport} onClick={() => setShowSharePopup(true)}>
                        {translate('offer.detail.header.actions.share')}
                    </Button>
                ) : null}

                {canEdit ? (
                    <Button onClick={() => navigateToOfferEdit(offer.id)}>{translate('actions.edit')}</Button>
                ) : null}

                {statusAction}

                <DropdownMenu
                    options={actions
                        .filter((action) => action.includeInDropdown)
                        .map((action) => ({
                            label: action.label,
                            onSelect: action.onSelect,
                            destructive: action.destructive,
                        }))}
                    align="end"
                    trigger={<IconButton emphasis="medium" Icon={IconDots} />}
                />
            </ButtonGroup>

            {showInitialEnquiryPopup ? (
                <InitialEnquiryPopup
                    offerId={offer.id}
                    onDismiss={() => setShowInitialEnquiryPopup(false)}
                    isOpen={showInitialEnquiryPopup}
                />
            ) : null}

            {showCancelPopup ? (
                <CancelPopup
                    isOpen={showCancelPopup}
                    onSubmit={onCancelPopupSubmit}
                    onDismiss={() => setShowCancelPopup(false)}
                    isSubmitting={updateOfferMutation.isPending}
                    text={getOfferCancelPopupText({
                        userType: role.user.userType,
                        offerState: offer.state,
                    })}
                />
            ) : null}

            {showReopenPopup ? (
                <SimpleConfirmPopup
                    isOpen={showReopenPopup}
                    onSubmit={onReopenPopupSubmit}
                    onDismiss={() => setShowReopenPopup(false)}
                    isSubmitting={reopenOfferMutation.isPending}
                    headline={translate('offer.detail.header.actions.reopenEnquiryPopup.headline')}
                    description={translate('offer.detail.header.actions.reopenEnquiryPopup.description')}
                    buttonText={translate('offer.detail.header.actions.reopenEnquiryPopup.button')}
                />
            ) : null}

            {showSharePopup ? (
                <ShareOrderPopup
                    isOpen={showSharePopup}
                    onSuccess={onShareOrderSuccess}
                    onDismiss={() => setShowSharePopup(false)}
                    offer={offer}
                />
            ) : null}

            {showHistoryOverlay ? (
                <OfferHistoryOverlay offer={offer} onClose={() => setShowHistoryOverlay(false)} />
            ) : null}
        </>
    );
};
